// AuctionResultsCard.js — Auction results display with Section A table and Section B comparison.
// Shows 91/182/364-day results, highlighted 364-day row, and PDF actions.

import React, { useState } from 'react';
import { FileText, Download, ExternalLink } from 'lucide-react';

function fmtPct(value) {
  if (value == null) return '--';
  return `${parseFloat(value).toFixed(3)}%`;
}

function fmtBn(value) {
  if (value == null) return '--';
  const num = parseFloat(value);
  if (num >= 1e9) return `KES ${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `KES ${(num / 1e6).toFixed(0)}M`;
  return `KES ${num.toLocaleString()}`;
}

const TENORS = [
  { key: '91', label: '91-Day', highlight: false },
  { key: '182', label: '182-Day', highlight: false },
  { key: '364', label: '364-Day', highlight: true },
];

export default function AuctionResultsCard({ auction, docs = [], onFetchResults, onGetPresignedUrl }) {
  const [fetching, setFetching] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);

  if (!auction) {
    return (
      <div className="bg-white border border-zinc-200 rounded-none p-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Auction Results
        </span>
        <p className="font-mono text-sm text-zinc-400 mt-3">
          No auction data available yet.
        </p>
      </div>
    );
  }

  const handleFetch = async () => {
    if (!onFetchResults) return;
    setFetching(true);
    try {
      await onFetchResults();
    } finally {
      setFetching(false);
    }
  };

  const handleViewPdf = async () => {
    if (!onGetPresignedUrl) return;
    setViewingPdf(true);
    try {
      const url = await onGetPresignedUrl();
      if (url) window.open(url, '_blank');
    } finally {
      setViewingPdf(false);
    }
  };

  const results = auction.results || {};
  const pdfDoc = docs.find(d => d.doc_type === 'auction_pdf');

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Auction Results
          </span>
        </div>
        <span className="font-mono text-[10px] text-zinc-400">
          {auction.auction_date || '--'}
        </span>
      </div>

      {/* Section A: Results Table */}
      <div className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
          Section A -- Primary Market
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                  Tenor
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  Weighted Avg Yield
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  Amount Accepted
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  Bids Received
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  Cover Ratio
                </th>
              </tr>
            </thead>
            <tbody>
              {TENORS.map(tenor => {
                const row = results[tenor.key] || {};
                return (
                  <tr
                    key={tenor.key}
                    className={`border-b ${
                      tenor.highlight
                        ? 'border-zinc-300 bg-zinc-50'
                        : 'border-zinc-100'
                    }`}
                  >
                    <td className={`py-2 px-2 font-mono text-xs ${
                      tenor.highlight ? 'font-semibold text-zinc-900' : 'text-zinc-700'
                    }`}>
                      {tenor.label}
                      {tenor.highlight && (
                        <span className="ml-2 text-[9px] font-mono uppercase px-1 py-0.5 bg-zinc-900 text-white">
                          Target
                        </span>
                      )}
                    </td>
                    <td className={`py-2 px-2 text-right font-mono tabular-nums ${
                      tenor.highlight ? 'font-semibold text-zinc-900' : 'text-zinc-700'
                    }`}>
                      {fmtPct(row.weighted_avg_yield)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-zinc-700">
                      {fmtBn(row.amount_accepted)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-zinc-700">
                      {fmtBn(row.bids_received)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-zinc-700">
                      {row.cover_ratio != null ? parseFloat(row.cover_ratio).toFixed(2) : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section B: Comparison Text */}
      {auction.comparison_text && (
        <div className="mb-4 border-t border-zinc-200 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            Section B -- Comparison
          </p>
          <p className="font-mono text-[11px] text-zinc-600 leading-relaxed">
            {auction.comparison_text}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 border-t border-zinc-200 pt-3">
        <button
          onClick={handleFetch}
          disabled={fetching}
          className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: '#4D4D4D' }}
        >
          <Download className="w-3.5 h-3.5" />
          {fetching ? 'Fetching...' : 'Fetch Results PDF'}
        </button>

        {pdfDoc && (
          <button
            onClick={handleViewPdf}
            disabled={viewingPdf}
            className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: '#4D4D4D' }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {viewingPdf ? 'Loading...' : 'View Raw PDF'}
          </button>
        )}
      </div>
    </div>
  );
}
